import React, { Component, PureComponent } from 'react';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';
import { validateSession } from '../utils/functions';
import { navigateTo } from '../utils/navigation';
import { storefrontUrl, copyLink } from '../utils/publicLinks';
import {
    getLibrary, setLibraryStatus, setGameCopies,
    getGameOwners, getGameProviders, createGameOwner, updateGameOwner, deleteGameOwner,
} from '../utils/reservationsApi';

// Ludoteca — qué juegos se pueden jugar en las mesas del local y de quién es cada copia.
//
// Lo que se marca acá es exactamente lo que el cliente ve al reservar una mesa, lo que aparece
// en el combo "Juego demo" del salón y lo que lista la página pública /ludoteca de la tienda.
//
// Rediseño de 2026-10. La versión anterior era UNA tabla con los ~1470 juegos de mesa del
// catálogo, un toggle por fila y un checkbox "ver sólo los de la ludoteca". Tres problemas:
//   - Para encontrar lo que tenemos y no está marcado había que recorrer el catálogo entero a
//     ojo. Y el sistema SÍ tiene pistas: productos auxiliares "DEMOCOPY - X" que se crearon
//     como nota de una copia en la estantería (siguieron naciendo después de la migración de
//     2026-08), juegos que se anotaron a mano en una mesa del salón porque no estaban en la
//     lista, y juegos que se desmarcaron pero siguen con copias. Ahora eso es la pestaña
//     "Para revisar", con el juego real sugerido y un botón para agregarlo.
//   - Toggle y copias eran dos controles para lo mismo: desmarcar dejaba la copia viva (había
//     81 juegos fuera de la ludoteca "con 1 copia"). Ahora "Sacar" saca también las copias, y
//     "Agregar" deja siempre al menos una.
//   - Ocultar un juego en la tienda lo sacaba de las reservas (el backend filtraba por
//     `display`). Eso se quitó en el backend: vender y prestar son ejes distintos. La pantalla
//     lo muestra como dato, no como problema.
//
// El motivo por el que un juego marcado NO llega al cliente lo decide el BACKEND y viaja en
// `notOfferedReason`: si la pantalla lo recalculara, bastaría que el servidor sumara una
// condición para que esto mostrara todo en verde mientras el picker no ofrece el juego.
//
// Los metadatos del juego (jugadores, duración, categoría) NO se editan acá a propósito: son
// del juego, no de la copia, y ya se editan en la ficha del producto. Esta pantalla sólo
// DELATA cuáles faltan.
//
// Sin modales de Bootstrap a propósito: navegar o recargar con un modal abierto deja el
// backdrop huérfano y congela la pantalla. Todo lo desplegable es inline.

const MAX_COPIES_FALLBACK = 99;
const PUBLIC_URL = storefrontUrl('/ludoteca');

// Tope de filas pintadas en "Agregar del catálogo": son ~1200 juegos sin marcar y con un
// buscador arriba nadie recorre más que esto. Pintarlos todos hacía lento cada tecla.
const CATALOG_ROW_LIMIT = 150;

// Descartes de "Para revisar" que el sistema no puede guardar de otra forma (un nombre anotado
// a mano en una mesa, un DEMOCOPY sin copias). Son por navegador: si se pierden, la pista vuelve
// a aparecer, que es inofensivo.
const DISMISSED_KEY = 'ludoteca.descartados';

// El nombre de categoría sale del mismo select que la ficha de producto (board_game.category).
const CATEGORY_NAMES = {
    1: 'Euro', 2: 'Party', 3: 'Familiar', 4: 'Abstracto', 5: 'Temático', 6: 'Investigación',
};

const METADATA_LABELS = {
    minPlayers: 'mín. jugadores',
    maxPlayers: 'máx. jugadores',
    gameDuration: 'duración',
    category: 'categoría',
};

const KIND_LABELS = { propio: 'La casa', proveedor: 'Proveedor', persona: 'Persona' };

// Las CLAVES las manda el backend (`notOfferedReason`): una razón nueva allá y no acá muestra el
// código crudo — feo, pero visible, que es mejor que la pantalla afirmando que está todo bien.
const NOT_OFFERED_TEXT = {
    'sin-copias': 'Sin copias: no aparece para reservar.',
};

const REVIEW_LABELS = {
    'auxiliar':         'Producto auxiliar DEMOCOPY',
    'auxiliar-marcado': 'Producto auxiliar DEMOCOPY en la ludoteca',
    'estuvo':           'Desmarcado pero con copia cargada',
    'mesa':             'Anotado a mano en una mesa',
};

const MUTED = { color: '#818ea3' };
const DANGER = { color: '#c4183c' };
const WARN = { color: '#b26b00' };

const ownerLabel = (kind, name) => (kind === 'propio' ? 'La casa' : name);
const kindLabel = (kind) => KIND_LABELS[kind] || kind;

// Normaliza un nombre para compararlo: sin tildes, sin el prefijo DEMOCOPY, sin puntuación.
// "DEMOCOPY - La isla Prohibida" y "La Isla Prohibida" quedan iguales.
function norm(text) {
    return String(text || '')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/^\s*democopy\b\s*[-–:]?\s*/, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}

// Coeficiente de Dice sobre bigramas: 1 = iguales, 0 = nada en común. Tolera los errores de
// tipeo reales de la estantería ("Blind Bussines", "Astro Kngihts", "Gradiant").
function dice(a, b) {
    if (a === b) return 1;
    if (a.length < 2 || b.length < 2) return 0;
    const grams = new Map();
    for (let i = 0; i < a.length - 1; i++) {
        const g = a.slice(i, i + 2);
        grams.set(g, (grams.get(g) || 0) + 1);
    }
    let hits = 0;
    for (let i = 0; i < b.length - 1; i++) {
        const g = b.slice(i, i + 2);
        const n = grams.get(g);
        if (n) { hits++; grams.set(g, n - 1); }
    }
    return (2 * hits) / (a.length + b.length - 2);
}

// Qué tan parecido es un nombre de juego a otro, de 0 a 1. Tres pruebas, se queda con la mejor:
//   - igualdad sin espacios ("Carpediem" = "Carpe Diem");
//   - parecido de la cadena entera (errores de tipeo);
//   - todas las palabras del buscado están en el candidato ("Exit: la casa de los enigmas"
//     está en "EXIT 12: La casa de los enigmas"; "Stone Age" está en "Stone Age: 10° Aniversario").
// Las palabras cortas (de, la, y) no cuentan: matchean medio catálogo.
function similarity(needle, candidate) {
    const a = needle.replace(/ /g, '');
    const b = candidate.replace(/ /g, '');
    if (!a || !b) return 0;
    if (a === b) return 1;
    let best = dice(a, b);
    const words = needle.split(' ').filter((w) => w.length >= 3);
    const cand = candidate.split(' ').filter((w) => w.length >= 2);
    if (words.length) {
        const found = words.filter((w) => cand.some((c) => c === w || (w.length >= 5 && dice(w, c) >= 0.7))).length;
        const cover = found / words.length;
        // Si están todas, se pondera por cuánto MÁS tiene el candidato: "Catan" se parece más a
        // "Catan" que a "Catan: Navegantes de Catan".
        if (cover === 1) best = Math.max(best, 0.8 + 0.2 * (words.length / Math.max(cand.filter((c) => c.length >= 3).length, 1)));
    }
    // Al revés: el candidato entero está dentro del buscado ("Stone Age" en "DEMOCOPY - Stone Age:
    // 10° Aniversario"). Puede ser otra edición, así que alcanza para SUGERIR pero no para dar
    // la pista por resuelta (ver RESOLVED_THRESHOLD).
    const candWords = cand.filter((c) => c.length >= 3);
    if (candWords.length >= 2 && candWords.every((c) => words.includes(c))) best = Math.max(best, 0.76);
    return best;
}

// Para sugerir alcanza un parecido razonable; para dar una pista por RESUELTA (y esconderla) se
// pide uno fuerte, porque esconder una pista de más es perder un juego de vista.
const MATCH_THRESHOLD = 0.75;
const RESOLVED_THRESHOLD = 0.85;

// Juegos del catálogo cuyo nombre se parece al buscado, mejor match primero. Se exige un largo
// mínimo para que "uno" no matchee medio catálogo.
function suggest(name, pool, limit = 4, threshold = MATCH_THRESHOLD) {
    const needle = norm(name);
    if (needle.length < 3) return [];
    return pool
        .map((g) => ({ g, score: similarity(needle, g.norm) }))
        .filter((x) => x.score >= threshold)
        .sort((a, b) => b.score - a.score)
        .slice(0, limit)
        .map((x) => x.g);
}

function readDismissed() {
    try {
        return JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]');
    } catch (e) {
        return [];
    }
}

function writeDismissed(list) {
    try {
        localStorage.setItem(DISMISSED_KEY, JSON.stringify(list));
    } catch (e) {
        // Sin storage el descarte dura hasta recargar. No vale la pena avisar.
    }
}

// Memoiza el último resultado: filtrar ~1470 juegos en cada render (hay uno por tecla) se nota.
function memoOne(fn) {
    let lastArgs = null;
    let lastResult;
    return (...args) => {
        if (!lastArgs || args.some((a, i) => a !== lastArgs[i])) {
            lastArgs = args;
            lastResult = fn(...args);
        }
        return lastResult;
    };
}

function renderMetadata(game) {
    const missing = game.missingMetadata || [];
    if (missing.length === 0) {
        const bg = game.boardGame || {};
        const players = bg.minPlayers === bg.maxPlayers ? `${bg.minPlayers}` : `${bg.minPlayers}-${bg.maxPlayers}`;
        return (
            <small style={MUTED}>
                {CATEGORY_NAMES[bg.category] || 'Sin categoría'} · {players} jug. · {bg.gameDuration} min
            </small>
        );
    }
    return (
        <small style={WARN} title="La tienda no puede filtrar por estos datos si faltan">
            Faltan datos: {missing.map((f) => METADATA_LABELS[f] || f).join(', ')}
        </small>
    );
}

const Pill = ({ children, style }) => (
    <span className="badge badge-pill badge-light ml-1" style={{ fontWeight: 500, ...style }}>{children}</span>
);

// ---------------------------------------------------------------------------
// Fila de la pestaña "En la ludoteca"
// ---------------------------------------------------------------------------

// PureComponent: sin esto, cada tecla del buscador reconciliaba todas las filas.
class LibraryRow extends PureComponent {
    render() {
        const { game, saving, expanded, houseId, maxCopies, onStep, onRemove, onExpand, onEdit, onTransfer } = this.props;
        const aviso = game.notOfferedReason ? NOT_OFFERED_TEXT[game.notOfferedReason] || game.notOfferedReason : null;
        const activos = (game.owners || []).filter((o) => o.active);
        const soloLaCasa = activos.every((o) => o.ownerId === houseId);
        const copias = game.copies || 0;

        return (
            <tr style={saving ? { opacity: 0.5 } : null}>
                <td className="text-left">
                    <div>
                        {game.name}
                        {!game.display && <Pill style={MUTED}>no se vende en la tienda</Pill>}
                        {game.placeholder && <Pill style={WARN}>producto auxiliar</Pill>}
                    </div>
                    <div>{renderMetadata(game)}</div>
                    {aviso && <div><small style={DANGER}>{aviso}</small></div>}
                    {game.placeholder && (
                        <button className="btn btn-sm btn-outline-warning mt-1" disabled={saving} onClick={() => onTransfer(game)}>
                            Pasar al juego real
                        </button>
                    )}
                </td>
                <td className="text-center" style={{ width: '170px' }}>
                    {soloLaCasa ? (
                        // Lo normal: todas las copias son de la casa y alcanza con sumar o restar.
                        <div className="btn-group btn-group-sm" role="group" aria-label="Copias">
                            <button className="btn btn-white" disabled={saving || copias <= 0}
                                title="Una copia menos" onClick={() => onStep(game, copias - 1)}>−</button>
                            <span className="btn btn-white disabled" style={{ minWidth: '42px', ...(copias === 0 ? DANGER : {}) }}>{copias}</span>
                            <button className="btn btn-white" disabled={saving || copias >= maxCopies}
                                title="Una copia más" onClick={() => onStep(game, copias + 1)}>+</button>
                        </div>
                    ) : (
                        <>
                            <strong style={copias === 0 ? DANGER : null}>{copias}</strong>
                            <div><small style={MUTED}>
                                {activos.map((o) => `${o.copies} de ${ownerLabel(o.ownerKind, o.ownerName).toLowerCase()}`).join(' · ')}
                            </small></div>
                        </>
                    )}
                    <div>
                        <button className="btn btn-link btn-sm p-0" disabled={saving} onClick={() => onExpand(game)}>
                            {expanded ? 'cerrar' : 'copias por dueño'}
                        </button>
                    </div>
                </td>
                <td className="text-right" style={{ width: '170px', whiteSpace: 'nowrap' }}>
                    <button className="btn btn-sm btn-white mr-1" title="Editar la ficha (jugadores, duración, categoría)"
                        onClick={() => onEdit(game)}>
                        <i className="material-icons">edit</i>
                    </button>
                    <button className="btn btn-sm btn-white" disabled={saving} onClick={() => onRemove(game)}>
                        Sacar
                    </button>
                </td>
            </tr>
        );
    }
}

// ---------------------------------------------------------------------------
// Fila de la pestaña "Agregar del catálogo"
// ---------------------------------------------------------------------------

class CatalogRow extends PureComponent {
    render() {
        const { game, saving, selected, onSelect, onAdd, onEdit } = this.props;
        return (
            <tr style={saving ? { opacity: 0.5 } : null}>
                <td style={{ width: '40px' }}>
                    <input type="checkbox" checked={selected} disabled={saving} onChange={() => onSelect(game)} />
                </td>
                <td className="text-left">
                    <div>
                        {game.name}
                        {!game.display && <Pill style={MUTED}>no se vende en la tienda</Pill>}
                    </div>
                    <div>{renderMetadata(game)}</div>
                </td>
                <td className="text-center" style={{ width: '110px' }}>
                    <small style={MUTED}>{game.stock > 0 ? `${game.stock} en stock` : 'sin stock'}</small>
                </td>
                <td className="text-right" style={{ width: '170px', whiteSpace: 'nowrap' }}>
                    <button className="btn btn-sm btn-white mr-1" title="Editar la ficha" onClick={() => onEdit(game)}>
                        <i className="material-icons">edit</i>
                    </button>
                    <button className="btn btn-sm btn-accent" disabled={saving} onClick={() => onAdd(game)}>
                        Agregar
                    </button>
                </td>
            </tr>
        );
    }
}

// ---------------------------------------------------------------------------

class LudotecaPage extends Component {

    constructor() {
        const cachedRole = Number(localStorage.getItem('role') || 0);
        super();
        this.state = {
            checked: !!localStorage.getItem('token'),
            role: cachedRole,
            tab: 'ludoteca',
            games: [],
            tableMentions: [],
            owners: [],
            providers: [],
            kinds: ['persona', 'proveedor'],
            maxCopies: MAX_COPIES_FALLBACK,
            loading: true,
            error: null,
            notice: null,
            search: '',
            category: '',
            onlyProblems: false,
            onlyForSale: false,
            onlyInStock: false,
            selected: {},
            // Códigos con una escritura en vuelo, para deshabilitar sus controles y no disparar
            // dos escrituras encimadas sobre la misma fila.
            savingCodes: {},
            // Progreso del agregado en lote: { done, total }.
            bulk: null,
            expandedCode: null,
            // Juego auxiliar (DEMOCOPY) cuya marca se está pasando al juego real.
            transferCode: null,
            transferQuery: '',
            dismissed: readDismissed(),
            savingOwner: false,
            newOwner: { name: '', kind: 'persona', providerId: '', contact: '' },
            editingOwnerId: null,
            editOwner: { name: '', contact: '' },
        };

        // Índice para comparar nombres. Se recalcula sólo cuando cambia la lista.
        this.indexar = memoOne((games) => {
            const all = games.map((g) => ({ ...g, norm: norm(g.name) }));
            const reales = all.filter((g) => !g.placeholder);
            return { all, reales, marcadosReales: reales.filter((g) => g.isDemo) };
        });

        this.revisar = memoOne((games, tableMentions, dismissed) => this.buildReview(games, tableMentions, dismissed));

        this.filtrarLudoteca = memoOne((games, search, category, onlyProblems) => {
            const term = norm(search);
            return games.filter((g) => {
                if (!g.isDemo) return false;
                if (term && !norm(g.name).includes(term)) return false;
                if (category && String((g.boardGame || {}).category) !== category) return false;
                if (onlyProblems && !g.notOfferedReason && !g.placeholder && !(g.missingMetadata || []).length) return false;
                return true;
            });
        });

        this.filtrarCatalogo = memoOne((games, search, category, onlyForSale, onlyInStock) => {
            const term = norm(search);
            return games.filter((g) => {
                if (g.isDemo || g.placeholder) return false;
                if (term && !norm(g.name).includes(term)) return false;
                if (category && String((g.boardGame || {}).category) !== category) return false;
                if (onlyForSale && !g.display) return false;
                if (onlyInStock && !(g.stock > 0)) return false;
                return true;
            });
        });
    }

    async componentDidMount() {
        const role = await validateSession(true);
        this.setState({ checked: true, role });
        this.load();
    }

    load = async () => {
        this.setState({ loading: true, error: null });
        try {
            // Los dueños se piden junto con el catálogo: hace falta saber cuál es la casa para
            // sumar y restar copias, y el desglose por juego los nombra.
            const [library, owners] = await Promise.all([getLibrary(), getGameOwners()]);
            this.setState({
                games: (library && library.games) || [],
                tableMentions: (library && library.tableMentions) || [],
                maxCopies: (library && library.maxCopies) || MAX_COPIES_FALLBACK,
                owners: (owners && owners.owners) || [],
                kinds: (owners && owners.kinds) || ['persona', 'proveedor'],
                loading: false,
            });
        } catch (err) {
            this.setState({ error: err.message || 'No se pudo cargar la ludoteca', loading: false });
        }
    };

    refreshOwners = async () => {
        const owners = await getGameOwners();
        this.setState({ owners: (owners && owners.owners) || [], kinds: (owners && owners.kinds) || this.state.kinds });
    };

    houseId() {
        const house = this.state.owners.find((o) => o.kind === 'propio');
        return house ? house.id : null;
    }

    // ---------------------------------------------------------------------------
    // "Para revisar": las pistas que tiene el sistema de juegos que están y no están marcados
    // ---------------------------------------------------------------------------

    buildReview(games, tableMentions, dismissed) {
        const { all, reales, marcadosReales } = this.indexar(games);
        const descartado = new Set(dismissed);
        const items = [];

        // ¿Ya hay en la ludoteca un juego real con este nombre? Entonces la pista está resuelta.
        const resuelto = (name) => suggest(name, marcadosReales, 1, RESOLVED_THRESHOLD).length > 0;

        for (const g of all) {
            if (g.placeholder) {
                if (g.isDemo) {
                    // En la ludoteca con el nombre "DEMOCOPY - ...": así lo ve el cliente.
                    items.push({ key: `aux-${g.code}`, kind: 'auxiliar-marcado', game: g, name: g.name,
                        suggestions: suggest(g.name, reales) });
                } else if (!resuelto(g.name) && !descartado.has(`aux-${g.code}`)) {
                    items.push({ key: `aux-${g.code}`, kind: 'auxiliar', game: g, name: g.name,
                        suggestions: suggest(g.name, reales) });
                }
            } else if (!g.isDemo && g.copies > 0) {
                // Se desmarcó con la versión anterior de esta pantalla, que dejaba la copia viva.
                // "Descartar" acá pone las copias en 0, así que la pista no vuelve.
                items.push({ key: `est-${g.code}`, kind: 'estuvo', game: g, name: g.name, suggestions: [g] });
            }
        }

        for (const m of tableMentions) {
            const key = `mesa-${norm(m.name)}`;
            if (descartado.has(key) || resuelto(m.name)) continue;
            items.push({ key, kind: 'mesa', game: null, name: m.name, times: m.times,
                suggestions: suggest(m.name, reales.filter((r) => !r.isDemo)) });
        }

        const order = { 'auxiliar-marcado': 0, 'mesa': 1, 'auxiliar': 2, 'estuvo': 3 };
        return items.sort((a, b) => order[a.kind] - order[b.kind] || a.name.localeCompare(b.name));
    }

    dismiss = (item) => {
        const dismissed = [...this.state.dismissed, item.key];
        writeDismissed(dismissed);
        this.setState({ dismissed });
    };

    // ---------------------------------------------------------------------------
    // Escrituras
    // ---------------------------------------------------------------------------

    markSaving(code, on) {
        this.setState((prev) => {
            const savingCodes = { ...prev.savingCodes };
            if (on) savingCodes[code] = true; else delete savingCodes[code];
            return { savingCodes };
        });
    }

    replaceRow(updated) {
        if (!updated) return;
        this.setState((prev) => ({ games: prev.games.map((g) => (g.code === updated.code ? updated : g)) }));
    }

    // Escritura puntual por juego: se manda sólo la fila tocada y se refresca con lo que devuelve
    // el backend. Nada de reemplazo total desde el snapshot del navegador, que pisaría en
    // silencio lo que otro haya cambiado en el interín.
    runOnGame = async (game, steps) => {
        this.markSaving(game.code, true);
        try {
            const updated = await steps();
            this.replaceRow(updated);
            return true;
        } catch (err) {
            this.setState({ error: `${game.name}: ${err.message || 'no se pudo guardar'}` });
            return false;
        } finally {
            this.markSaving(game.code, false);
        }
    };

    // Agregar = marcar + que haya al menos una copia. El backend crea la copia de la casa sólo si
    // el juego NUNCA tuvo copias; si tuvo y están en 0 (se sacó antes), la ponemos acá: quien
    // aprieta "Agregar" está diciendo que el juego está en la estantería.
    addSteps = (game) => async () => {
        const marked = await setLibraryStatus(game.code, { active: true });
        let row = marked && marked.game;
        if (row && !row.copies) {
            const houseId = this.houseId();
            if (houseId) {
                const withCopy = await setGameCopies(game.code, { ownerId: houseId, copies: 1 });
                row = (withCopy && withCopy.game) || row;
            }
        }
        return row;
    };

    // Sacar = desmarcar + poner en 0 las copias activas. Antes desmarcar dejaba la copia viva y
    // el juego quedaba "fuera de la ludoteca con 1 copia", que no significa nada.
    removeSteps = (game) => async () => {
        let row = (await setLibraryStatus(game.code, { active: false })).game;
        for (const o of (game.owners || []).filter((x) => x.active)) {
            row = (await setGameCopies(game.code, { ownerId: o.ownerId, copies: 0 })).game || row;
        }
        return row;
    };

    addGame = (game) => this.runOnGame(game, this.addSteps(game));

    removeGame = (game) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`¿Sacar "${game.name}" de la ludoteca? Deja de ofrecerse para reservar y en el salón.`)) return;
        this.runOnGame(game, this.removeSteps(game));
    };

    // Descartar una pista "estuvo": no está, así que sus copias van a 0 y no vuelve a aparecer.
    discardCopies = (game) => this.runOnGame(game, this.removeSteps(game));

    stepCopies = (game, copies) => {
        const houseId = this.houseId();
        if (!houseId || copies < 0 || copies > this.state.maxCopies) return;
        this.runOnGame(game, async () => (await setGameCopies(game.code, { ownerId: houseId, copies })).game);
    };

    addSelected = async () => {
        const codes = Object.keys(this.state.selected).filter((c) => this.state.selected[c]);
        const games = this.state.games.filter((g) => codes.includes(String(g.code)));
        if (!games.length) return;

        this.setState({ bulk: { done: 0, total: games.length }, error: null, notice: null });
        const fallidos = [];
        // De a uno y en orden: son pocas decenas y así un error queda atado a su juego.
        for (const g of games) {
            const ok = await this.runOnGame(g, this.addSteps(g));
            if (!ok) fallidos.push(g.name);
            this.setState((prev) => ({ bulk: { ...prev.bulk, done: prev.bulk.done + 1 } }));
        }
        this.setState({
            bulk: null,
            selected: {},
            error: fallidos.length ? `No se pudieron agregar: ${fallidos.join(', ')}` : null,
            notice: `${games.length - fallidos.length} juego${games.length - fallidos.length === 1 ? '' : 's'} agregado${games.length - fallidos.length === 1 ? '' : 's'} a la ludoteca.`,
        });
    };

    // Pasar la marca de un producto auxiliar "DEMOCOPY - X" al juego real X: agregar el real y
    // sacar el auxiliar. Es lo que antes se hacía a mano en dos pasos, y 3 veces quedó hecha
    // la mitad (el juego desapareció de la ludoteca sin que nadie lo notara).
    transfer = async (placeholder, real) => {
        const current = this.state.games.find((g) => g.code === real.code) || real;
        if (!current.isDemo || !current.copies) {
            const ok = await this.runOnGame(current, this.addSteps(current));
            if (!ok) return;
        }
        if (placeholder.isDemo || placeholder.copies > 0) {
            await this.runOnGame(placeholder, this.removeSteps(placeholder));
        }
        this.setState({ transferCode: null, transferQuery: '', notice: `"${real.name}" quedó en la ludoteca en lugar de "${placeholder.name}".` });
    };

    // ---------------------------------------------------------------------------
    // Copias por dueño (el caso poco común: juegos prestados por amigos o proveedores)
    // ---------------------------------------------------------------------------

    // Handlers estables (métodos de clase, no flechas inline): las filas son PureComponent y una
    // función nueva por render las haría reconciliar todas en cada tecla del buscador.
    openTransfer = (game) => this.setState({ transferCode: game.code, transferQuery: '' });

    toggleSelected = (game) => this.setState((prev) => ({ selected: { ...prev.selected, [game.code]: !prev.selected[game.code] } }));

    expand = (game) => this.setState((prev) => ({ expandedCode: prev.expandedCode === game.code ? null : game.code }));

    editProduct = (game) => navigateTo(`/editProduct?code=${game.code}`);

    commitCopies = (game, ownerId, raw) => {
        // Un campo VACÍO no es un cero. `Number('')` da 0, y acá 0 significa "el dueño se llevó
        // la copia": si era la única, el juego sale de las reservas. Un `<input type="number">`
        // entrega '' para cualquier contenido inválido, así que el tipo del input no protege.
        const texto = String(raw).trim();
        if (texto === '' || Number.isNaN(Number(texto))) return;

        const copies = Number(texto);
        const current = (game.owners || []).find((o) => o.ownerId === ownerId);
        if (!Number.isInteger(copies) || copies < 0 || copies > this.state.maxCopies) return;
        if (current && Number(current.copies) === copies && (copies > 0) === current.active) return;

        return this.runOnGame(game, async () => (await setGameCopies(game.code, { ownerId, copies })).game);
    };

    addOwnerToGame = (game, ownerId) => {
        if (!ownerId) return;
        this.runOnGame(game, async () => (await setGameCopies(game.code, { ownerId: Number(ownerId), copies: 1 })).game);
    };

    renderOwnerEditor(game, colSpan) {
        const saving = Boolean(this.state.savingCodes[game.code]);
        const yaEstan = new Set((game.owners || []).map((o) => o.ownerId));
        const disponibles = this.state.owners.filter((o) => o.active && !yaEstan.has(o.id));

        return (
            <tr key={`${game.code}-owners`} style={saving ? { opacity: 0.5 } : null}>
                <td colSpan={colSpan} className="text-left" style={{ background: '#f8f9fa' }}>
                    <div className="mb-2"><strong>Copias de "{game.name}" por dueño</strong></div>

                    {(game.owners || []).length === 0 && (
                        <div style={MUTED} className="mb-2">Todavía no hay copias cargadas.</div>
                    )}

                    {(game.owners || []).map((o) => (
                        <div key={o.ownerId} className="d-flex align-items-center mb-2">
                            <div style={{ width: '220px' }}>
                                {ownerLabel(o.ownerKind, o.ownerName)}
                                {o.ownerKind !== 'propio' && <> <small style={MUTED}>({kindLabel(o.ownerKind)})</small></>}
                            </div>
                            {/* No controlado: con `key` atada al valor del servidor, el campo se
                                remonta cuando ese valor cambia y no hace falta guardar el
                                borrador en el state de la página. */}
                            <input
                                key={`${game.code}:${o.ownerId}:${o.copies}:${o.active}`}
                                type="number"
                                min="0"
                                max={this.state.maxCopies}
                                className="form-control form-control-sm text-center"
                                style={{ width: '90px' }}
                                defaultValue={o.copies}
                                disabled={saving}
                                onBlur={(e) => this.commitCopies(game, o.ownerId, e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                            />
                            {!o.active && (
                                <small style={{ ...MUTED, marginLeft: '10px' }}>
                                    se la llevó — poné un número para volver a contarla
                                </small>
                            )}
                        </div>
                    ))}

                    {disponibles.length > 0 && (
                        <div className="d-flex align-items-center mt-3">
                            <select
                                className="form-control form-control-sm"
                                style={{ width: '260px' }}
                                value=""
                                disabled={saving}
                                onChange={(e) => this.addOwnerToGame(game, e.target.value)}
                            >
                                <option value="">Agregar copia de otro dueño...</option>
                                {disponibles.map((o) => (
                                    <option key={o.id} value={o.id}>{o.name} ({kindLabel(o.kind)})</option>
                                ))}
                            </select>
                        </div>
                    )}
                    {disponibles.length === 0 && this.state.owners.filter((o) => o.active).length <= 1 && (
                        <small style={MUTED}>
                            Si un amigo o un proveedor prestó una copia, cargalo en la pestaña Dueños y
                            después agregalo acá.
                        </small>
                    )}
                </td>
            </tr>
        );
    }

    renderTransfer(game, colSpan) {
        const { transferQuery } = this.state;
        const { reales } = this.indexar(this.state.games);
        const sugeridos = suggest(game.name, reales);
        const term = norm(transferQuery);
        const buscados = term.length >= 2
            ? reales.filter((r) => r.norm.includes(term)).slice(0, 8)
            : [];
        const opciones = [...sugeridos, ...buscados.filter((b) => !sugeridos.includes(b))];

        return (
            <tr key={`${game.code}-transfer`}>
                <td colSpan={colSpan} className="text-left" style={{ background: '#fff8e6' }}>
                    <div className="mb-2">
                        <strong>¿Qué juego es "{game.name}"?</strong>{' '}
                        <small style={MUTED}>Se agrega el juego real a la ludoteca y se saca el producto auxiliar.</small>
                    </div>
                    <input
                        type="text" className="form-control form-control-sm mb-2" style={{ maxWidth: '360px' }}
                        placeholder="Buscar otro juego del catálogo..."
                        value={transferQuery}
                        onChange={(e) => this.setState({ transferQuery: e.target.value })}
                    />
                    {opciones.map((r) => (
                        <button key={r.code} className="btn btn-sm btn-white mr-2 mb-2" onClick={() => this.transfer(game, r)}>
                            {r.isDemo ? '✓ ' : '+ '}{r.name}
                            {r.isDemo && <small style={MUTED}> (ya está: se saca el auxiliar)</small>}
                            {!r.isDemo && !r.display && <small style={MUTED}> (no se vende)</small>}
                        </button>
                    ))}
                    {opciones.length === 0 && (
                        <div style={MUTED}>
                            <small>
                                No encuentro el juego en el catálogo. Dalo de alta como producto (puede quedar
                                oculto en la tienda) y después volvé acá.{' '}
                                <button className="btn btn-link btn-sm p-0" onClick={() => navigateTo('/addProduct')}>Nuevo producto</button>
                            </small>
                        </div>
                    )}
                    <div>
                        <button className="btn btn-link btn-sm p-0" onClick={() => this.setState({ transferCode: null, transferQuery: '' })}>Cancelar</button>
                    </div>
                </td>
            </tr>
        );
    }

    // ---------------------------------------------------------------------------
    // ABM de dueños
    // ---------------------------------------------------------------------------

    openOwnersTab = async () => {
        this.setState({ tab: 'duenos' });
        // Los proveedores se piden recién acá, y una sola vez: la mayoría de las visitas no
        // abren esta pestaña.
        if (this.state.providers.length === 0) {
            try {
                const data = await getGameProviders();
                this.setState({ providers: (data && data.providers) || data || [] });
            } catch (err) {
                this.setState({ error: err.message || 'No se pudieron cargar los proveedores' });
            }
        }
        this.refreshOwners();
    };

    runOwnerAction = async (call, mensajeError) => {
        if (this.state.savingOwner) return;
        this.setState({ savingOwner: true, error: null });
        try {
            await call();
            await this.refreshOwners();
        } catch (err) {
            this.setState({ error: err.message || mensajeError });
        } finally {
            this.setState({ savingOwner: false });
        }
    };

    saveNewOwner = () => {
        const { newOwner } = this.state;
        return this.runOwnerAction(async () => {
            await createGameOwner({
                name: newOwner.name,
                kind: newOwner.kind,
                providerId: newOwner.kind === 'proveedor' ? Number(newOwner.providerId) : undefined,
                contact: newOwner.contact || undefined,
            });
            this.setState({ newOwner: { name: '', kind: 'persona', providerId: '', contact: '' } });
        }, 'No se pudo crear el dueño');
    };

    saveEditOwner = (owner) => {
        const { editOwner } = this.state;
        return this.runOwnerAction(async () => {
            await updateGameOwner(owner.id, { name: editOwner.name, contact: editOwner.contact });
            this.setState({ editingOwnerId: null });
            // El nombre del dueño también aparece en el desglose de cada juego.
            await this.load();
        }, 'No se pudo editar el dueño');
    };

    removeOwner = (owner) => this.runOwnerAction(
        () => deleteGameOwner(owner.id),
        // El backend bloquea el borrado si el dueño todavía tiene copias: el mensaje que viaja
        // explica qué hacer antes (poner esas copias en 0).
        'No se pudo eliminar el dueño',
    );

    renderOwnerRow(o) {
        const { editingOwnerId, editOwner, savingOwner } = this.state;

        if (editingOwnerId === o.id) {
            return (
                <tr key={o.id}>
                    <td className="text-left">
                        <input
                            type="text" className="form-control form-control-sm"
                            value={editOwner.name}
                            onChange={(e) => this.setState({ editOwner: { ...editOwner, name: e.target.value } })}
                        />
                    </td>
                    <td>{kindLabel(o.kind)}</td>
                    <td>
                        <input
                            type="text" className="form-control form-control-sm" placeholder="Contacto"
                            value={editOwner.contact}
                            onChange={(e) => this.setState({ editOwner: { ...editOwner, contact: e.target.value } })}
                        />
                    </td>
                    <td>{o.games}</td>
                    <td>{o.totalCopies}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn btn-sm btn-accent mr-1" disabled={savingOwner || !editOwner.name.trim()}
                            onClick={() => this.saveEditOwner(o)}>Guardar</button>
                        <button className="btn btn-sm btn-white" disabled={savingOwner}
                            onClick={() => this.setState({ editingOwnerId: null })}>Cancelar</button>
                    </td>
                </tr>
            );
        }

        return (
            <tr key={o.id}>
                <td className="text-left">{o.name}</td>
                <td>{kindLabel(o.kind)}</td>
                <td>{o.contact || <span style={MUTED}>—</span>}</td>
                <td>{o.games}</td>
                <td>{o.totalCopies}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                    <button
                        className="btn btn-sm btn-white mr-1"
                        title="Editar nombre y contacto"
                        disabled={savingOwner}
                        onClick={() => this.setState({ editingOwnerId: o.id, editOwner: { name: o.name, contact: o.contact || '' } })}
                    >
                        <i className="material-icons">edit</i>
                    </button>
                    {/* La casa no se puede eliminar: es el dueño al que se le atribuyen las
                        copias propias y lo siembra la migración. */}
                    {o.kind !== 'propio' && (
                        <button
                            className="btn btn-sm btn-white"
                            title={o.totalCopies > 0 ? 'Primero poné en 0 las copias que le pertenecen' : 'Eliminar dueño'}
                            disabled={savingOwner}
                            onClick={() => this.removeOwner(o)}
                        >
                            <i className="material-icons">delete</i>
                        </button>
                    )}
                </td>
            </tr>
        );
    }

    renderOwnersTab() {
        const { owners, providers, newOwner, kinds, savingOwner } = this.state;
        const esProveedor = newOwner.kind === 'proveedor';

        return (
            <div className="card card-small mb-4">
                <div className="card-body">
                    <p style={MUTED}>
                        No todo lo que está en la estantería es nuestro. Cargá acá a los proveedores y
                        amigos que prestaron juegos; después, en cada juego, "copias por dueño" dice
                        de quién es cada copia y a quién devolverle qué.
                    </p>

                    <table className="table table-sm mb-3">
                        <thead className="bg-light">
                            <tr>
                                <th className="border-0 text-left">Dueño</th>
                                <th className="border-0">Tipo</th>
                                <th className="border-0">Contacto</th>
                                <th className="border-0">Juegos</th>
                                <th className="border-0">Copias</th>
                                <th className="border-0"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {owners.filter((o) => o.active).map((o) => this.renderOwnerRow(o))}
                        </tbody>
                    </table>

                    <div className="row">
                        <div className="col-md-3 mb-2">
                            <input
                                type="text" className="form-control form-control-sm" placeholder="Nombre"
                                value={newOwner.name}
                                onChange={(e) => this.setState({ newOwner: { ...newOwner, name: e.target.value } })}
                            />
                        </div>
                        <div className="col-md-2 mb-2">
                            {/* Los tipos los manda el backend: agregar uno nuevo allá no obliga a
                                deployar esta pantalla. */}
                            <select
                                className="form-control form-control-sm"
                                value={newOwner.kind}
                                onChange={(e) => this.setState({ newOwner: { ...newOwner, kind: e.target.value } })}
                            >
                                {kinds.map((k) => <option key={k} value={k}>{kindLabel(k)}</option>)}
                            </select>
                        </div>
                        {esProveedor && (
                            <div className="col-md-3 mb-2">
                                {/* El proveedor se ELIGE de los que ya existen: copiar el nombre a
                                    mano daría una segunda fuente de verdad para el mismo proveedor. */}
                                <select
                                    className="form-control form-control-sm"
                                    value={newOwner.providerId}
                                    onChange={(e) => this.setState({ newOwner: { ...newOwner, providerId: e.target.value } })}
                                >
                                    <option value="">Elegí el proveedor...</option>
                                    {providers.map((p) => (
                                        <option key={p.providerId} value={p.providerId}>{p.name}</option>
                                    ))}
                                </select>
                                {providers.length === 0 && (
                                    // El filtro por categoría es estricto (ENI-187): si nadie
                                    // etiquetó proveedores como "juegos" la lista viene vacía.
                                    <small style={MUTED}>
                                        Ningún proveedor está clasificado como "juegos". Se etiquetan
                                        desde el ABM de proveedores.
                                    </small>
                                )}
                            </div>
                        )}
                        <div className="col-md-2 mb-2">
                            <input
                                type="text" className="form-control form-control-sm" placeholder="Contacto"
                                value={newOwner.contact}
                                onChange={(e) => this.setState({ newOwner: { ...newOwner, contact: e.target.value } })}
                            />
                        </div>
                        <div className="col-md-2 mb-2">
                            <button
                                className="btn btn-sm btn-accent"
                                disabled={savingOwner || !newOwner.name.trim() || (esProveedor && !newOwner.providerId)}
                                onClick={this.saveNewOwner}
                            >
                                Agregar dueño
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ---------------------------------------------------------------------------
    // Pestañas
    // ---------------------------------------------------------------------------

    renderCategorySelect() {
        return (
            <select
                className="form-control"
                value={this.state.category}
                onChange={(e) => this.setState({ category: e.target.value })}
            >
                <option value="">Todas las categorías</option>
                {Object.entries(CATEGORY_NAMES).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
        );
    }

    renderSearch(placeholder) {
        return (
            <input
                type="text"
                className="form-control"
                placeholder={placeholder}
                value={this.state.search}
                onChange={(e) => this.setState({ search: e.target.value })}
            />
        );
    }

    renderLibraryTab() {
        const { games, search, category, onlyProblems, savingCodes, expandedCode, transferCode, maxCopies } = this.state;
        const visible = this.filtrarLudoteca(games, search, category, onlyProblems);
        const houseId = this.houseId();

        return (
            <>
                <div className="row mb-3">
                    <div className="col-md-5 mb-2">{this.renderSearch('Buscar en la ludoteca...')}</div>
                    <div className="col-md-3 mb-2">{this.renderCategorySelect()}</div>
                    <div className="col-md-4 mb-2 d-flex align-items-center">
                        <label className="mb-0" style={{ cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={onlyProblems}
                                onChange={(e) => this.setState({ onlyProblems: e.target.checked })}
                            />
                            {' '}Sólo los que tienen algo para arreglar
                        </label>
                    </div>
                </div>

                <div className="card card-small mb-4">
                    <div className="card-body p-0 pb-3 text-center">
                        <table className="table mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th scope="col" className="border-0 text-left">Juego</th>
                                    <th scope="col" className="border-0">Copias</th>
                                    <th scope="col" className="border-0"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.length === 0 && (
                                    <tr><td colSpan="3" style={MUTED}>
                                        {games.some((g) => g.isDemo)
                                            ? 'Ningún juego coincide con el filtro.'
                                            : 'La ludoteca está vacía. Agregá juegos desde "Agregar del catálogo".'}
                                    </td></tr>
                                )}
                                {visible.reduce((rows, g) => {
                                    rows.push(
                                        <LibraryRow
                                            key={g.code}
                                            game={g}
                                            saving={Boolean(savingCodes[g.code])}
                                            expanded={expandedCode === g.code}
                                            houseId={houseId}
                                            maxCopies={maxCopies}
                                            onStep={this.stepCopies}
                                            onRemove={this.removeGame}
                                            onExpand={this.expand}
                                            onEdit={this.editProduct}
                                            onTransfer={this.openTransfer}
                                        />
                                    );
                                    if (transferCode === g.code) rows.push(this.renderTransfer(g, 3));
                                    if (expandedCode === g.code) rows.push(this.renderOwnerEditor(g, 3));
                                    return rows;
                                }, [])}
                            </tbody>
                        </table>
                    </div>
                </div>
            </>
        );
    }

    renderReviewTab(items) {
        const { savingCodes, transferCode, games } = this.state;
        const byCode = new Map(games.map((g) => [g.code, g]));

        if (items.length === 0) {
            return (
                <div className="card card-small mb-4"><div className="card-body" style={MUTED}>
                    No hay nada para revisar. Si encontrás en la estantería un juego que no está en la
                    ludoteca, agregalo desde "Agregar del catálogo".
                </div></div>
            );
        }

        return (
            <div className="card card-small mb-4">
                <div className="card-body p-0 pb-3">
                    <p className="px-3 pt-3" style={MUTED}>
                        Pistas de juegos que probablemente están en la estantería y no figuran (o figuran
                        mal) en la ludoteca. Agregá el juego correcto o descartá la pista.
                    </p>
                    <table className="table mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th className="border-0 text-left">Pista</th>
                                <th className="border-0 text-left">Juego sugerido</th>
                                <th className="border-0"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.reduce((rows, item) => {
                                const placeholder = item.game && byCode.get(item.game.code);
                                const saving = placeholder && savingCodes[placeholder.code];
                                rows.push(
                                    <tr key={item.key} style={saving ? { opacity: 0.5 } : null}>
                                        <td className="text-left" style={{ width: '40%' }}>
                                            <div>{item.name}</div>
                                            <small style={item.kind === 'auxiliar-marcado' ? WARN : MUTED}>
                                                {REVIEW_LABELS[item.kind]}
                                                {item.kind === 'mesa' && ` · ${item.times} ${item.times === 1 ? 'vez' : 'veces'}`}
                                                {item.kind === 'auxiliar-marcado' && ' · el cliente lo ve con ese nombre'}
                                            </small>
                                        </td>
                                        <td className="text-left">
                                            {item.kind === 'estuvo' ? (
                                                <button className="btn btn-sm btn-accent" disabled={saving}
                                                    onClick={() => this.addGame(placeholder)}>
                                                    Volver a agregar
                                                </button>
                                            ) : item.suggestions.length > 0 ? (
                                                item.suggestions.map((s) => {
                                                    const real = byCode.get(s.code) || s;
                                                    return (
                                                        <button key={s.code} className="btn btn-sm btn-white mr-2 mb-1"
                                                            disabled={Boolean(savingCodes[s.code]) || (real.isDemo && !placeholder)}
                                                            title={real.isDemo ? 'Ya está en la ludoteca: se saca el producto auxiliar' : 'Agregar a la ludoteca'}
                                                            onClick={() => (placeholder ? this.transfer(placeholder, real) : this.addGame(real))}>
                                                            {real.isDemo ? '✓ ' : '+ '}{s.name}
                                                            {real.isDemo && <small style={MUTED}> (ya está)</small>}
                                                        </button>
                                                    );
                                                })
                                            ) : (
                                                <small style={MUTED}>No hay un juego con ese nombre en el catálogo.</small>
                                            )}
                                            {placeholder && item.kind !== 'estuvo' && (
                                                <div>
                                                    <button className="btn btn-link btn-sm p-0"
                                                        onClick={() => this.setState({ transferCode: transferCode === placeholder.code ? null : placeholder.code, transferQuery: '' })}>
                                                        buscar otro juego
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                        <td className="text-right" style={{ width: '120px' }}>
                                            {item.kind === 'estuvo' && (
                                                <button className="btn btn-sm btn-white" disabled={saving}
                                                    title="No está en la estantería: se ponen sus copias en 0"
                                                    onClick={() => this.discardCopies(placeholder)}>Descartar</button>
                                            )}
                                            {(item.kind === 'mesa' || item.kind === 'auxiliar') && (
                                                <button className="btn btn-sm btn-white" onClick={() => this.dismiss(item)}>Descartar</button>
                                            )}
                                        </td>
                                    </tr>
                                );
                                if (placeholder && transferCode === placeholder.code) rows.push(this.renderTransfer(placeholder, 3));
                                return rows;
                            }, [])}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    renderCatalogTab() {
        const { games, search, category, onlyForSale, onlyInStock, savingCodes, selected, bulk } = this.state;
        const all = this.filtrarCatalogo(games, search, category, onlyForSale, onlyInStock);
        const visible = all.slice(0, CATALOG_ROW_LIMIT);
        const selectedCount = Object.values(selected).filter(Boolean).length;
        const allVisibleSelected = visible.length > 0 && visible.every((g) => selected[g.code]);

        return (
            <>
                <p style={MUTED}>
                    Los juegos de mesa del catálogo que NO están en la ludoteca. Marcá los que estén en la
                    estantería y agregalos de una vez. No hace falta que se vendan en la tienda.
                </p>
                <div className="row mb-2">
                    <div className="col-md-5 mb-2">{this.renderSearch('Buscar en el catálogo...')}</div>
                    <div className="col-md-3 mb-2">{this.renderCategorySelect()}</div>
                    <div className="col-md-4 mb-2 d-flex align-items-center">
                        <label className="mb-0 mr-3" style={{ cursor: 'pointer' }}>
                            <input type="checkbox" checked={onlyForSale}
                                onChange={(e) => this.setState({ onlyForSale: e.target.checked })} />
                            {' '}Se venden en la tienda
                        </label>
                        <label className="mb-0" style={{ cursor: 'pointer' }}>
                            <input type="checkbox" checked={onlyInStock}
                                onChange={(e) => this.setState({ onlyInStock: e.target.checked })} />
                            {' '}Con stock
                        </label>
                    </div>
                </div>

                <div className="d-flex align-items-center mb-3" style={{ position: 'sticky', top: 0, zIndex: 2, background: '#f5f6f8', padding: '8px 0' }}>
                    <button className="btn btn-accent mr-3" disabled={!selectedCount || Boolean(bulk)} onClick={this.addSelected}>
                        {bulk ? `Agregando ${bulk.done} de ${bulk.total}...` : `Agregar ${selectedCount || ''} a la ludoteca`}
                    </button>
                    {selectedCount > 0 && !bulk && (
                        <button className="btn btn-link btn-sm" onClick={() => this.setState({ selected: {} })}>Limpiar selección</button>
                    )}
                    <small style={MUTED} className="ml-auto">
                        {all.length > visible.length
                            ? `Mostrando ${visible.length} de ${all.length}: buscá para acotar.`
                            : `${all.length} juego${all.length === 1 ? '' : 's'}`}
                    </small>
                </div>

                <div className="card card-small mb-4">
                    <div className="card-body p-0 pb-3 text-center">
                        <table className="table mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="border-0" style={{ width: '40px' }}>
                                        <input type="checkbox" title="Seleccionar los que se ven" checked={allVisibleSelected}
                                            onChange={() => this.setState((prev) => {
                                                const next = { ...prev.selected };
                                                visible.forEach((g) => { next[g.code] = !allVisibleSelected; });
                                                return { selected: next };
                                            })} />
                                    </th>
                                    <th className="border-0 text-left">Juego</th>
                                    <th className="border-0">Tienda</th>
                                    <th className="border-0"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.length === 0 && (
                                    <tr><td colSpan="4" style={MUTED}>Ningún juego coincide con el filtro.</td></tr>
                                )}
                                {visible.map((g) => (
                                    <CatalogRow
                                        key={g.code}
                                        game={g}
                                        saving={Boolean(savingCodes[g.code])}
                                        selected={Boolean(selected[g.code])}
                                        onSelect={this.toggleSelected}
                                        onAdd={this.addGame}
                                        onEdit={this.editProduct}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </>
        );
    }

    renderBody(review) {
        const { loading, error, notice, tab } = this.state;

        if (loading) return <p style={MUTED}>Cargando...</p>;

        return (
            <>
                {error && (
                    <div className="alert alert-danger">
                        {error}{' '}
                        <button className="btn btn-sm btn-white ml-2" onClick={this.load}>Recargar</button>
                        <button className="btn btn-sm btn-link ml-1" onClick={() => this.setState({ error: null })}>Cerrar</button>
                    </div>
                )}
                {notice && !error && (
                    <div className="alert alert-success">
                        {notice}
                        <button className="btn btn-sm btn-link ml-1" onClick={() => this.setState({ notice: null })}>Cerrar</button>
                    </div>
                )}
                {tab === 'ludoteca' && this.renderLibraryTab()}
                {tab === 'revisar' && this.renderReviewTab(review)}
                {tab === 'catalogo' && this.renderCatalogTab()}
                {tab === 'duenos' && this.renderOwnersTab()}
            </>
        );
    }

    copyPublicLink = () => copyLink(PUBLIC_URL);

    setTab = (tab) => {
        if (tab === 'duenos') { this.openOwnersTab(); return; }
        // El buscador y la categoría son por pestaña en la cabeza de quien la usa: arrastrar el
        // texto de una a otra daba listas vacías sin explicación.
        this.setState({ tab, search: '', category: '', transferCode: null });
    };

    render() {
        if (!this.state.checked) return (<div></div>);

        const { games, tableMentions, dismissed, tab, loading } = this.state;
        const enLudoteca = games.filter((g) => g.isDemo);
        const ofrecidos = enLudoteca.filter((g) => !g.notOfferedReason).length;
        const review = this.revisar(games, tableMentions, dismissed);

        const tabs = [
            { id: 'ludoteca', label: 'En la ludoteca', count: enLudoteca.length },
            { id: 'revisar', label: 'Para revisar', count: review.length, warn: review.length > 0 },
            { id: 'catalogo', label: 'Agregar del catálogo' },
            { id: 'duenos', label: 'Dueños' },
        ];

        return (
            <div className="container-fluid">
                <div className="row">
                    <SidebarComponent itemSelected={35} role={this.state.role} />
                    <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4">
                            <HeaderComponent title="Ludoteca" subtitle="Juegos para jugar en el local" />
                            <div className="d-flex flex-wrap align-items-center mb-3" style={{ marginTop: '-1rem' }}>
                                <p className="mb-0 mr-3" style={MUTED}>
                                    Lo que está acá es lo que el cliente puede elegir al reservar, lo que
                                    aparece en el salón y lo que lista la página pública.
                                    {!loading && <> <strong>{ofrecidos}</strong> juego{ofrecidos === 1 ? '' : 's'} para jugar hoy.</>}
                                </p>
                                <a className="btn btn-sm btn-white mr-2" href={PUBLIC_URL} target="_blank" rel="noopener noreferrer">
                                    Ver página pública
                                </a>
                                <button className="btn btn-sm btn-white" onClick={this.copyPublicLink}>Copiar link</button>
                            </div>

                            <ul className="nav nav-tabs mb-3">
                                {tabs.map((t) => (
                                    <li className="nav-item" key={t.id}>
                                        <button
                                            type="button"
                                            className={`nav-link btn btn-link${tab === t.id ? ' active' : ''}`}
                                            onClick={() => this.setTab(t.id)}
                                        >
                                            {t.label}
                                            {!loading && t.count != null && (
                                                <span className={`badge badge-pill ml-1 ${t.warn ? 'badge-warning' : 'badge-light'}`}>{t.count}</span>
                                            )}
                                        </button>
                                    </li>
                                ))}
                            </ul>

                            {this.renderBody(review)}
                        </div>
                    </main>
                </div>
            </div>
        );
    }
}

export default LudotecaPage;
